import React, { useEffect, useState } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import StepShell from './StepShell';

import SelectFromField from 'CommonComponents/SelectFromField';
import TextFormField from 'CommonComponents/TextFormField';

import { useToast } from 'Hooks/useToast';

const BasicsSchema = Yup.object().shape({
    name: Yup.string().required($t('This value is required')),
    locale: Yup.string().required($t('This value is required')),
    currency_id: Yup.string().required($t('This value is required')),
});

/**
 * Project basics. The organisation name is display-only and comes from the
 * Organisation record, so it is never part of the submitted payload.
 */
export default function BasicsStep({ step, urls, deferring, onDefer, onSaved }) {
    const [data, setData] = useState(null);
    const toast = useToast();

    useEffect(() => {
        axios
            .get(urls.data)
            .then(({ data }) => setData(data))
            .catch(() => toast.error($t('Something went wrong. Please try again.')));
    }, [urls.data]);

    if (!data) {
        return (
            <StepShell step={step} deferring={deferring} saving={true} onDefer={onDefer} onNext={() => {}} nextLabel={$t('Save and continue')}>
                <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>
            </StepShell>
        );
    }

    return (
        <Formik
            initialValues={{
                name: data.name ?? '',
                locale: data.locale ?? '',
                currency_id: data.currency_id ? String(data.currency_id) : '',
            }}
            validationSchema={BasicsSchema}
            onSubmit={(values, actions) => {
                axios
                    .put(urls.update, values)
                    .then(({ data }) => onSaved(data))
                    .catch((e) => {
                        if (e.response?.data?.errors) {
                            actions.setErrors(e.response.data.errors);
                        } else {
                            toast.error($t('Something went wrong. Please try again.'));
                        }
                    })
                    .finally(() => actions.setSubmitting(false));
            }}
        >
            {({ isSubmitting, handleSubmit }) => (
                <StepShell
                    step={step}
                    lede={$t('Confirm the essentials. Locale and currency drive every figure, date and report downstream, so they come first.')}
                    deferring={deferring}
                    saving={isSubmitting}
                    onDefer={onDefer}
                    onNext={handleSubmit}
                    nextDisabled={step.read_only}
                    nextLabel={$t('Save and continue')}
                >
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6 tw-max-w-3xl">
                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                            {/* Display only — no control to label, so this is not a <label>. */}
                            <span className="text-input__label">{$t('Organisation name')}</span>
                            <p className="tw-text-sm tw-text-charcoal tw-font-semibold tw-mb-0">{data.organisation_name}</p>
                        </div>

                        <TextFormField label={$t('Project name')} required={true} type="text" name="name" disabled={step.read_only} />

                        <div>
                            <SelectFromField label={$t('Project locale')} required={true} as="select" name="locale" disabled={step.read_only}>
                                <option value="">{$t('Select a locale')}</option>
                                {(data.locales ?? []).map((locale) => (
                                    <option key={locale.code} value={locale.code}>
                                        {locale.name}
                                    </option>
                                ))}
                            </SelectFromField>
                            <p className="tw-mt-1.5 tw-text-2xs tw-text-charcoal-50">{$t('Sets date and number formats across the project.')}</p>
                        </div>

                        <div>
                            <SelectFromField
                                label={$t('Local currency')}
                                required={true}
                                as="select"
                                name="currency_id"
                                disabled={step.read_only || data.currency_locked}
                            >
                                <option value="">{$t('Select a currency')}</option>
                                {(data.currencies ?? []).map((currency) => (
                                    <option key={currency.id} value={String(currency.id)}>
                                        {currency.code || currency.name}
                                    </option>
                                ))}
                            </SelectFromField>
                            <p className="tw-mt-1.5 tw-text-2xs tw-text-charcoal-50">
                                {$t('The base currency for all budgets and reports. It cannot be changed once figures exist.')}
                            </p>
                        </div>
                    </div>
                </StepShell>
            )}
        </Formik>
    );
}
